import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import NewsletterSection from "../home/components/NewsletterSection";
import NewsSubmissionCTA from "@/components/feature/NewsSubmissionCTA";
import { useSectorStats } from "../../hooks/useSectorStats";
import { useArticles } from "../../hooks/useArticles";
import { toNewsItems } from "../../lib/articleAdapter";
import { useSponsors } from "../../hooks/useSponsors";
import SEO from "@/components/SEO";

const JAC_POWERBI_URL =
  "https://app.powerbi.com/view?r=eyJrIjoiODc3ZmFlNmUtYzZhZC00ZDNjLTgxZTYtZjk5MjFjZGNkNWM3IiwidCI6ImFiMGY0OWUzLWJhNjctNGQzNC1iYzEwLWZmNTgzMmJhZTdhZSIsImMiOjJ9";

/* ─── Types ─── */
interface AdBannerProps {
  sponsor?: {
    banner_url?: string;
    company_name?: string;
    website_url?: string;
    notes?: string;
  };
  emptyTitle?: string;
  emptyDesc?: string;
}

/* ─── AdBanner ─── */
function AdBanner({ sponsor, emptyTitle, emptyDesc }: AdBannerProps) {
  if (!sponsor?.banner_url) {
    return (
      <Link
        to="/publicidad"
        className="relative rounded-xl overflow-hidden border border-dashed border-white/20 group cursor-pointer hover:border-red-400/50 hover:bg-white/5 transition-all block"
      >
        <div className="block h-28 flex flex-col items-center justify-center text-center p-4">
          <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg mb-2">
            <i className="ri-megaphone-line text-white/60 text-sm" />
          </div>
          <p
            className="text-white/80 font-semibold text-xs"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {emptyTitle || "¿Tu empresa aquí?"}
          </p>
          <p
            className="text-white/40 text-[10px] mt-0.5"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {emptyDesc || "Anuncia en el portal líder del sector aeronáutico"}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <a
      href={sponsor.website_url || "#"}
      target="_blank"
      rel="nofollow noreferrer"
      className="relative rounded-xl overflow-hidden border border-white/10 group cursor-pointer hover:-translate-y-0.5 transition-transform block"
    >
      <>
        <div
          className="absolute top-2 left-2 z-10 bg-black/60 text-white text-[9px] font-bold px-2 py-0.5 rounded-full tracking-widest uppercase"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          Publicidad
        </div>
        <img
          src={sponsor.banner_url}
          alt={sponsor.company_name || "Publicidad"}
          className="w-full h-28 object-cover object-center group-hover:scale-105 transition-transform duration-500 opacity-80"
        />
        <div className="p-3 bg-gradient-to-r from-[#0d2547] to-[#1a3a6b]">
          <p
            className="text-white font-bold text-xs"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {sponsor.company_name}
          </p>
          {sponsor.notes && (
            <p
              className="text-blue-200/60 text-[10px] mt-0.5"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {sponsor.notes}
            </p>
          )}
        </div>
      </>
    </a>
  );
}

/* ─── Main Page ─── */
export default function Estadisticas() {
  const [modalOpen, setModalOpen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const { stats, loading: statsLoading } = useSectorStats();
  const { articles: estadisticasArticles, loading: articlesLoading } = useArticles("Estadísticas", false);
  const { sponsors: statsSponsors } = useSponsors("Estadisticas");

  const newsItems = toNewsItems(estadisticasArticles).slice(0, 4);

  const dataSource = stats?.data_source || "Junta de Aviación Civil (JAC)";
  const dataSourceDate = stats?.data_source_date
    ? new Date(stats.data_source_date).toLocaleDateString("es-DO", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "febrero 2026";

  return (
    <main className="min-h-screen bg-[#071529]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "WebPage",
            name: "Estadísticas del Sector Aéreo",
            description: "Análisis y reportes sobre datos del sector aéreo dominicano.",
            url: 'https://aviacioninsightrd.com/estadisticas',
          },
        ]}
      />
      <Navbar />

      {/* ─── Hero ─── */}
      <section className="relative pt-28 md:pt-32 pb-10 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "linear-gradient(#2563eb 1px, transparent 1px), linear-gradient(90deg, #2563eb 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-blue-400 text-sm hover:text-blue-300 cursor-pointer whitespace-nowrap">
              Inicio
            </Link>
            <i className="ri-arrow-right-s-line text-blue-600" />
            <span className="text-slate-400 text-sm whitespace-nowrap">Estadísticas</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div className="min-w-0">
              <h1
                className="text-white font-bold"
                style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(1.5rem, 5vw, 3rem)" }}
              >
                ESTADÍSTICAS DEL SECTOR AÉREO
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* ─── News Section + Sponsors Sidebar ─── */}
      <section className="pb-10 md:pb-14">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main news */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-slate-500 text-xs mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Análisis y reportes sobre datos del sector aéreo dominicano
                  </p>
                </div>
              </div>

              {articlesLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white/5 border border-white/8 rounded-xl p-4 animate-pulse">
                      <div className="h-32 bg-white/10 rounded-lg mb-3" />
                      <div className="h-4 bg-white/10 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-white/10 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : newsItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {newsItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/articulo/${item.slug}`}
                      className="bg-white/5 border border-white/8 rounded-xl overflow-hidden hover:bg-white/10 hover:border-blue-500/30 transition-all group text-left cursor-pointer block"
                    >
                      <div className="h-32 md:h-36 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-[10px] text-blue-300 bg-blue-600/20 border border-blue-500/30 px-2 py-0.5 rounded-full uppercase font-semibold tracking-wider">
                          {item.date || "Estadísticas"}
                        </span>
                        <p
                          className="text-white font-semibold text-sm mt-2 line-clamp-2 group-hover:text-blue-300 transition-colors"
                          style={{ fontFamily: "'Sora', sans-serif" }}
                        >
                          {item.title}
                        </p>
                        <p className="text-slate-400 text-xs mt-1.5 line-clamp-2">{item.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="bg-white/5 border border-white/8 rounded-xl p-8 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-4">
                    <i className="ri-newspaper-line text-slate-500 text-2xl" />
                  </div>
                  <p className="text-slate-400 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    No hay noticias de estadísticas aún
                  </p>
                  <p className="text-slate-600 text-xs mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Próximamente se publicarán análisis y reportes sobre datos del sector
                  </p>
                </div>
              )}
            </div>

            {/* Sponsor sidebar */}
            <aside className="w-full lg:w-64 flex flex-col gap-4 flex-shrink-0">
              <h4
                className="text-slate-400 text-xs font-semibold uppercase tracking-wider hidden lg:block"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Patrocinadores
              </h4>
              <AdBanner
                sponsor={statsSponsors[0]}
                emptyTitle="Sector Aeronáutico"
                emptyDesc="Anuncia tu empresa aeronáutica aquí"
              />
              <AdBanner
                sponsor={statsSponsors[1]}
                emptyTitle="Consultoría Aeronáutica"
                emptyDesc="Análisis estratégico del sector aéreo dominicano"
              />
              <AdBanner
                sponsor={statsSponsors[2]}
                emptyTitle="Retail Aeroportuario"
                emptyDesc="Duty free y experiencias de compra en terminales"
              />
            </aside>
          </div>
        </div>
      </section>

      {/* ─── PowerBI JAC Dashboard ─── */}
      <section className="pb-10 md:pb-14">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4 md:mb-5">
            <div>
              <h2
                className="text-white font-bold flex items-center gap-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "clamp(1.1rem, 3vw, 1.5rem)" }}
              >
                <i className="ri-bar-chart-2-line text-blue-400" />
                CIFRAS DEL SECTOR AÉREO · DASHBOARD JAC
              </h2>
              <p className="text-slate-500 text-xs mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Dashboard oficial de la Junta de Aviación Civil · Datos en tiempo real
              </p>
            </div>
            <a
              href={JAC_POWERBI_URL}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 border border-blue-500/40 hover:border-blue-400 hover:bg-blue-600/20 text-blue-300 hover:text-blue-200 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line" />
              Abrir en nueva pestaña
            </a>
          </div>

          <div className="bg-white rounded-xl overflow-hidden border border-white/10">
            {/* iframe header bar */}
            <div className="bg-[#0B1F3A] px-3 md:px-4 py-2.5 md:py-3 flex items-center justify-between border-b border-blue-900/40">
              <div className="flex items-center gap-2 md:gap-2.5 min-w-0">
                <div className="w-7 h-7 flex items-center justify-center bg-blue-600 rounded-md flex-shrink-0">
                  <i className="ri-bar-chart-2-line text-white text-xs" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-xs truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Dashboard JAC · Tráfico Aerocomercial RD
                  </p>
                  <p className="text-blue-300/50 text-[9px] hidden sm:block" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Sistema Dinámico de Visualización de Datos · Fuente oficial
                  </p>
                </div>
              </div>
              <span className="text-[9px] text-blue-300/40 bg-blue-900/30 px-2 py-1 rounded hidden md:inline-block">
                app.powerbi.com
              </span>
            </div>

            {/* iframe container */}
            <div className="relative w-full h-[240px] sm:h-[340px] md:h-[420px] lg:h-[560px]">
              {iframeLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#F3F4F6] z-10 gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                    <i className="ri-loader-4-line text-blue-500 text-2xl md:text-3xl animate-spin" />
                  </div>
                  <p className="text-slate-500 text-xs md:text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Cargando dashboard oficial JAC...
                  </p>
                  <p className="text-slate-400 text-[10px] md:text-xs" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Los datos se actualizan directamente desde la Junta de Aviación Civil
                  </p>
                </div>
              )}
              <iframe
                src={JAC_POWERBI_URL}
                title="Dashboard JAC - Tráfico Aerocomercial República Dominicana"
                className="w-full h-full border-0"
                allowFullScreen
                onLoad={() => setIframeLoading(false)}
              />
            </div>
          </div>

          {/* Source note */}
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-slate-500 text-xs" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <i className="ri-information-line mr-1" />
              Fuente: <strong className="text-slate-400">{dataSource}</strong> · Dashboard incrustado en tiempo real desde PowerBI.
            </p>
            <a
              href={JAC_POWERBI_URL}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex-shrink-0 text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              Ver fuente original <i className="ri-external-link-line" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Source note ─── */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-6 pb-8 md:pb-10">
        <p className="text-slate-600 text-xs" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <i className="ri-information-line mr-1" />
          Fuente: <strong className="text-slate-500">{dataSource}</strong> · Datos actualizados al {dataSourceDate}. Los valores son aproximados al momento de publicación.
          <a
            href={JAC_POWERBI_URL}
            target="_blank"
            rel="nofollow noreferrer"
            className="ml-2 text-blue-500 hover:text-blue-300 cursor-pointer whitespace-nowrap"
          >
            Fuente oficial <i className="ri-external-link-line" />
          </a>
        </p>
      </div>

      <NewsletterSection />
      <Footer />

      {/* ─── PowerBI Modal ─── */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-3 md:p-4 backdrop-blur-sm"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-[#0B1F3A] rounded-2xl overflow-hidden w-full max-w-6xl mx-auto relative flex flex-col"
            style={{ maxHeight: "90vh", height: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-blue-900/40">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center bg-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-bar-chart-2-line text-white text-xs md:text-sm" />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-white font-semibold text-xs md:text-sm truncate"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    Sistema Dinámico de Visualización de Datos
                  </p>
                  <p
                    className="text-blue-300/60 text-[9px] md:text-[10px]"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    JAC · República Dominicana · Tráfico Aerocomercial
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={JAC_POWERBI_URL}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="hidden sm:flex px-3 py-1.5 bg-blue-600/30 border border-blue-500/40 text-blue-300 text-xs rounded-lg items-center gap-1 cursor-pointer whitespace-nowrap hover:bg-blue-600/50 transition-colors"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  Abrir en nueva pestaña <i className="ri-external-link-line" />
                </a>
                <button
                  onClick={() => setModalOpen(false)}
                  className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-white cursor-pointer transition-colors"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
            <div className="relative w-full" style={{ height: "calc(85vh - 56px)" }}>
              {iframeLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#071529] z-10 gap-3 md:gap-4">
                  <i className="ri-loader-4-line text-blue-400 text-2xl md:text-3xl animate-spin" />
                  <p
                    className="text-blue-200/60 text-xs md:text-sm"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    Cargando dashboard oficial JAC...
                  </p>
                </div>
              )}
              <iframe
                src={JAC_POWERBI_URL}
                title="Dashboard JAC - Tráfico Aerocomercial República Dominicana"
                className="w-full h-full border-0"
                allowFullScreen
                onLoad={() => setIframeLoading(false)}
              />
            </div>
          </div>
        </div>
      )}
    </main>
  );
}